import { useEffect, useState } from "react";
import {
  CheckCircle2,
  CreditCard,
  RefreshCw,
  ShieldCheck,
  X,
} from "lucide-react";
import axios from "axios";

const getApiErrorMessage = (error, fallback = "Request gagal diproses.") => {
  const detail = error?.response?.data?.detail;

  if (typeof detail === "string" && detail.trim()) {
    return detail;
  }

  if (detail && typeof detail === "object" && !Array.isArray(detail)) {
    if (typeof detail.message === "string" && detail.message.trim()) {
      return detail.message;
    }

    if (typeof detail.msg === "string" && detail.msg.trim()) {
      return detail.msg;
    }
  }

  if (Array.isArray(detail)) {
    const messages = detail
      .map((item) => {
        if (typeof item === "string") return item;
        if (item && typeof item.msg === "string") return item.msg;
        if (item && typeof item.message === "string") return item.message;
        return "";
      })
      .filter(Boolean);

    if (messages.length > 0) {
      return messages.join(", ");
    }
  }

  if (
    typeof error?.response?.data?.message === "string" &&
    error.response.data.message.trim()
  ) {
    return error.response.data.message;
  }

  return fallback;
};

export default function PlatformPaymentsPage() {
  const [pendingPayments, setPendingPayments] = useState([]);
  const [pendingLoading, setPendingLoading] = useState(true);
  const [pendingLoadingMore, setPendingLoadingMore] = useState(false);
  const [pendingHasMore, setPendingHasMore] = useState(false);
  const [pendingNextCursor, setPendingNextCursor] = useState(null);
  const [pendingCount, setPendingCount] = useState(0);
  const [pendingError, setPendingError] = useState("");
  const [paymentActionId, setPaymentActionId] = useState("");
  const [rejectingPaymentId, setRejectingPaymentId] = useState("");
  const [rejectReason, setRejectReason] = useState("");

  const [proofPayment, setProofPayment] = useState(null);
  const [proofUrl, setProofUrl] = useState("");
  const [proofLoading, setProofLoading] = useState(false);
  const [proofError, setProofError] = useState("");

  const [paymentHistory, setPaymentHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [historyError, setHistoryError] = useState("");

  const loadPendingPayments = async () => {
    setPendingLoading(true);
    setPendingError("");
    setPendingNextCursor(null);
    setPendingHasMore(false);

    try {
      const response = await axios.get("/api/billing/qris/pending", {
        params: {
          limit: 20,
        },
      });

      setPendingPayments(response.data?.items || []);
      setPendingHasMore(Boolean(response.data?.has_more));
      setPendingNextCursor(response.data?.next_cursor || null);
      setPendingCount(
        typeof response.data?.pending_count === "number"
          ? response.data.pending_count
          : response.data?.count || 0
      );
    } catch (err) {
      setPendingError(
        getApiErrorMessage(
          err,
          "Unable to load pending subscription payments."
        )
      );
      setPendingCount(0);
    } finally {
      setPendingLoading(false);
    }
  };

  const loadMorePendingPayments = async () => {
    if (
      pendingLoading ||
      pendingLoadingMore ||
      !pendingHasMore ||
      !pendingNextCursor
    ) {
      return;
    }

    setPendingLoadingMore(true);
    setPendingError("");

    try {
      const response = await axios.get("/api/billing/qris/pending", {
        params: {
          limit: 20,
          cursor: pendingNextCursor,
        },
      });

      setPendingPayments((current) => [
        ...current,
        ...(response.data?.items || []),
      ]);

      setPendingHasMore(Boolean(response.data?.has_more));
      setPendingNextCursor(response.data?.next_cursor || null);

      if (typeof response.data?.pending_count === "number") {
        setPendingCount(response.data.pending_count);
      }
    } catch (err) {
      setPendingError(
        getApiErrorMessage(
          err,
          "Unable to load more pending subscription payments."
        )
      );
    } finally {
      setPendingLoadingMore(false);
    }
  };

  const loadPaymentHistory = async () => {
    setHistoryLoading(true);
    setHistoryError("");

    try {
      const response = await axios.get("/api/billing/payment-history");
      setPaymentHistory(response.data?.items || []);
    } catch (err) {
      setHistoryError(
        getApiErrorMessage(err, "Unable to load payment history.")
      );
    } finally {
      setHistoryLoading(false);
    }
  };

  const loadAllPayments = async () => {
    await Promise.all([loadPendingPayments(), loadPaymentHistory()]);
  };

  const openPaymentProof = async (payment) => {
    setProofPayment(payment);
    setProofUrl("");
    setProofError("");
    setProofLoading(true);

    try {
      const response = await axios.get(
        `/api/billing/qris/${payment.id}/proof`,
        {
          responseType: "blob",
        }
      );

      const url = URL.createObjectURL(response.data);
      setProofUrl(url);
    } catch (err) {
      setProofError(
        getApiErrorMessage(
          err,
          "Bukti pembayaran gagal dimuat."
        )
      );
    } finally {
      setProofLoading(false);
    }
  };

  const closePaymentProof = () => {
    if (proofUrl) {
      URL.revokeObjectURL(proofUrl);
    }

    setProofPayment(null);
    setProofUrl("");
    setProofError("");
    setProofLoading(false);
  };

  useEffect(() => {
    loadAllPayments();
  }, []);

  const approvePayment = async (payment) => {
    const confirmed = window.confirm(
      `Pastikan pembayaran ${
        payment.plan_name || payment.plan_code || "subscription"
      } sebesar Rp ${Number(payment.amount || 0).toLocaleString(
        "id-ID"
      )} sudah diverifikasi melalui mutasi bank/QRIS.\n\nAktifkan subscription sekarang?`
    );

    if (!confirmed) return;

    setPaymentActionId(payment.id);
    setPendingError("");

    try {
      await axios.post(`/api/billing/qris/${payment.id}/approve`);
      await Promise.all([loadPendingPayments(), loadPaymentHistory()]);
    } catch (err) {
      setPendingError(
        getApiErrorMessage(err, "Pembayaran gagal disetujui.")
      );
    } finally {
      setPaymentActionId("");
    }
  };

  const startRejectPayment = (payment) => {
    setRejectingPaymentId(payment.id);
    setRejectReason("");
    setPendingError("");
  };

  const cancelRejectPayment = () => {
    if (paymentActionId) return;

    setRejectingPaymentId("");
    setRejectReason("");
  };

  const rejectPayment = async (payment) => {
    const reason = rejectReason.trim();

    if (!reason) {
      setPendingError("Alasan penolakan wajib diisi.");
      return;
    }

    if (reason.length > 500) {
      setPendingError("Alasan penolakan maksimal 500 karakter.");
      return;
    }

    setPaymentActionId(payment.id);
    setPendingError("");

    try {
      await axios.post(`/api/billing/qris/${payment.id}/reject`, {
        reason,
      });

      setRejectingPaymentId("");
      setRejectReason("");

      await Promise.all([loadPendingPayments(), loadPaymentHistory()]);
    } catch (err) {
      setPendingError(
        getApiErrorMessage(err, "Pembayaran gagal ditolak.")
      );
    } finally {
      setPaymentActionId("");
    }
  };

  const getStatusClass = (status) => {
    switch (status) {
      case "PAID":
        return "bg-emerald-500/15 text-emerald-400";
      case "REJECTED":
        return "bg-red-500/15 text-red-400";
      case "WAITING_VERIFICATION":
        return "bg-amber-500/15 text-amber-400";
      case "PENDING":
        return "bg-blue-500/15 text-blue-400";
      case "EXPIRED":
        return "bg-muted text-muted-foreground";
      default:
        return "bg-muted text-muted-foreground";
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-primary/15 flex items-center justify-center">
            <CreditCard className="text-primary" size={22} />
          </div>

          <div>
            <h1 className="text-2xl font-heading font-bold">
              Payments
            </h1>

            <p className="text-sm text-muted-foreground">
              Verify subscription payments and review payment history.
            </p>
          </div>
        </div>
      </div>

      {/* PAYMENT VERIFICATION */}
      <div className="rounded-xl border border-border bg-card p-6 space-y-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <CreditCard size={18} />
              <span className="font-semibold">
                Payment Verification
              </span>

              {pendingCount > 0 && (
                <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-xs font-medium text-amber-400">
                  {pendingCount}
                </span>
              )}
            </div>

            <p className="mt-1 text-sm text-muted-foreground">
              Verify subscription payments manually against the bank or QRIS mutation.
            </p>
          </div>

          <button
            type="button"
            onClick={loadPendingPayments}
            disabled={pendingLoading}
            className="inline-flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm hover:bg-muted disabled:opacity-50"
          >
            <RefreshCw
              size={15}
              className={pendingLoading ? "animate-spin" : ""}
            />
            Refresh
          </button>
        </div>

        {pendingError && (
          <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-300">
            {pendingError}
          </div>
        )}

        {pendingLoading ? (
          <div className="py-8 text-center text-sm text-muted-foreground">
            Loading payment verification queue...
          </div>
        ) : pendingPayments.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border p-8 text-center">
            <ShieldCheck
              size={24}
              className="mx-auto mb-2 text-muted-foreground"
            />
            <p className="font-medium">
              No payment waiting for verification
            </p>
            <p className="mt-1 text-sm text-muted-foreground">
              New QRIS payments submitted by owners will appear here.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {pendingPayments.map((payment) => (
              <div
                key={payment.id}
                className="rounded-xl border border-border p-4"
              >
                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                  <div className="min-w-0 space-y-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-semibold">
                        {payment.tenant?.brand_name ||
                          payment.tenant?.name ||
                          payment.tenant_id ||
                          "Unknown business"}
                      </span>

                      <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-xs font-medium text-amber-400">
                        WAITING VERIFICATION
                      </span>
                    </div>

                    <div className="grid gap-3 text-sm sm:grid-cols-2 lg:grid-cols-3">
                      <div>
                        <p className="text-xs text-muted-foreground">
                          Owner
                        </p>
                        <p className="font-medium">
                          {payment.owner?.name || "—"}
                        </p>
                        <p className="text-muted-foreground">
                          {payment.owner?.email || "—"}
                        </p>
                        <p className="text-muted-foreground">
                          {payment.owner?.phone || "—"}
                        </p>
                      </div>

                      <div>
                        <p className="text-xs text-muted-foreground">
                          Plan
                        </p>
                        <p className="font-medium">
                          {payment.plan_name ||
                            payment.plan_code ||
                            "—"}
                        </p>
                        <p className="text-muted-foreground">
                          {payment.billing_interval || "—"}
                        </p>
                      </div>

                      <div>
                        <p className="text-xs text-muted-foreground">
                          Amount
                        </p>
                        <p className="font-semibold">
                          {payment.currency || "IDR"}{" "}
                          {Number(payment.amount || 0).toLocaleString(
                            "id-ID"
                          )}
                        </p>
                      </div>

                      <div>
                        <p className="text-xs text-muted-foreground">
                          Submitted
                        </p>
                        <p>
                          {payment.payment_submitted_at
                            ? new Date(
                                payment.payment_submitted_at
                              ).toLocaleString("id-ID")
                            : "—"}
                        </p>
                      </div>

                      <div>
                        <p className="text-xs text-muted-foreground">
                          Invoice
                        </p>
                        <p className="break-all font-mono text-xs">
                          {payment.id || "—"}
                        </p>
                      </div>

                      <div>
                        <p className="text-xs text-muted-foreground">
                          Merchant Ref
                        </p>
                        <p className="break-all font-mono text-xs">
                          {payment.merchant_ref || "—"}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="flex shrink-0 flex-col gap-2 sm:flex-row lg:flex-col">
                    {payment.payment_proof?.available && (
                      <button
                        type="button"
                        onClick={() => openPaymentProof(payment)}
                        disabled={paymentActionId === payment.id}
                        className="inline-flex items-center justify-center gap-2 rounded-lg border border-primary/40 px-4 py-2 text-sm font-medium text-primary hover:bg-primary/10 disabled:opacity-50"
                      >
                        Lihat Bukti Pembayaran
                      </button>
                    )}

                    <button
                      type="button"
                      onClick={() => approvePayment(payment)}
                      disabled={paymentActionId === payment.id}
                      className="inline-flex items-center justify-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-500 disabled:opacity-50"
                    >
                      <CheckCircle2 size={15} />
                      {paymentActionId === payment.id
                        ? "Processing..."
                        : "Approve"}
                    </button>

                    <button
                      type="button"
                      onClick={() => startRejectPayment(payment)}
                      disabled={paymentActionId === payment.id}
                      className="inline-flex items-center justify-center gap-2 rounded-lg border border-red-500/40 px-4 py-2 text-sm font-medium text-red-300 hover:bg-red-500/10 disabled:opacity-50"
                    >
                      <X size={15} />
                      Reject
                    </button>
                  </div>
                </div>

                {rejectingPaymentId === payment.id && (
                  <div className="mt-4 rounded-lg border border-red-500/30 bg-red-500/5 p-4">
                    <label className="block text-sm font-medium">
                      Rejection reason
                      <span className="ml-1 text-red-400">*</span>
                    </label>

                    <textarea
                      value={rejectReason}
                      onChange={(e) => setRejectReason(e.target.value)}
                      maxLength={500}
                      rows={3}
                      placeholder="Contoh: pembayaran belum ditemukan pada mutasi rekening."
                      className="mt-2 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-ring"
                      disabled={paymentActionId === payment.id}
                    />

                    <div className="mt-2 flex items-center justify-between gap-3">
                      <span className="text-xs text-muted-foreground">
                        {rejectReason.length}/500
                      </span>

                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={cancelRejectPayment}
                          disabled={paymentActionId === payment.id}
                          className="rounded-lg border border-border px-3 py-2 text-sm hover:bg-muted disabled:opacity-50"
                        >
                          Cancel
                        </button>

                        <button
                          type="button"
                          onClick={() => rejectPayment(payment)}
                          disabled={
                            paymentActionId === payment.id ||
                            !rejectReason.trim()
                          }
                          className="rounded-lg bg-red-600 px-3 py-2 text-sm font-medium text-white hover:bg-red-500 disabled:opacity-50"
                        >
                          {paymentActionId === payment.id
                            ? "Processing..."
                            : "Confirm Reject"}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}

            {pendingHasMore && (
              <div className="mt-4 flex justify-center">
                <button
                  type="button"
                  onClick={loadMorePendingPayments}
                  disabled={pendingLoadingMore}
                  className="inline-flex items-center justify-center gap-2 rounded-lg border border-border px-4 py-2 text-sm font-medium hover:bg-muted disabled:opacity-50"
                >
                  {pendingLoadingMore ? (
                    <>
                      <RefreshCw size={15} className="animate-spin" />
                      Loading...
                    </>
                  ) : (
                    "Load More"
                  )}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {proofPayment && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
          <div className="relative flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-xl border border-border bg-card shadow-2xl">
            <div className="flex items-center justify-between border-b border-border p-4">
              <div>
                <h2 className="font-semibold">Bukti Pembayaran</h2>
                <p className="mt-1 text-xs text-muted-foreground">
                  {proofPayment.payment_proof?.filename || "Bukti pembayaran"}
                </p>
              </div>

              <button
                type="button"
                onClick={closePaymentProof}
                className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-border hover:bg-muted"
                aria-label="Tutup"
              >
                <X size={18} />
              </button>
            </div>

            <div className="flex min-h-[300px] items-center justify-center overflow-auto p-4">
              {proofLoading && (
                <p className="text-sm text-muted-foreground">
                  Memuat bukti pembayaran...
                </p>
              )}

              {!proofLoading && proofError && (
                <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-4 text-sm text-red-300">
                  {proofError}
                </div>
              )}

              {!proofLoading && !proofError && proofUrl && (
                <img
                  src={proofUrl}
                  alt="Bukti pembayaran"
                  className="max-h-[70vh] max-w-full rounded-lg object-contain"
                />
              )}
            </div>
          </div>
        </div>
      )}

      {/* PAYMENT HISTORY */}
      <div className="rounded-xl border border-border bg-card">
        <div className="flex items-start justify-between gap-4 p-5 border-b border-border">
          <div>
            <h2 className="font-semibold">Payment History</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Historical subscription payment transactions.
            </p>
          </div>

          <button
            type="button"
            onClick={loadPaymentHistory}
            disabled={historyLoading}
            className="inline-flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm hover:bg-muted disabled:opacity-50"
          >
            <RefreshCw
              size={15}
              className={historyLoading ? "animate-spin" : ""}
            />
            Refresh
          </button>
        </div>

        {historyError && (
          <div className="m-5 rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-300">
            {historyError}
          </div>
        )}

        {historyLoading ? (
          <div className="p-8 text-center text-sm text-muted-foreground">
            Loading payment history...
          </div>
        ) : paymentHistory.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground">
            No payment history yet.
          </div>
        ) : (
          <div className="divide-y divide-border">
            {paymentHistory.map((payment) => {
              const status = payment.status || "UNKNOWN";

              return (
                <div key={payment.id} className="p-5">
                  <div className="flex flex-col gap-4">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-semibold">
                        {payment.tenant?.brand_name ||
                          payment.tenant?.name ||
                          payment.tenant_id ||
                          "Unknown business"}
                      </span>

                      <span
                        className={`rounded-full px-2 py-0.5 text-xs font-medium ${getStatusClass(
                          status
                        )}`}
                      >
                        {status}
                      </span>
                    </div>

                    <div className="grid gap-3 text-sm sm:grid-cols-2 lg:grid-cols-4">
                      <div>
                        <p className="text-xs text-muted-foreground">
                          Owner
                        </p>
                        <p className="font-medium">
                          {payment.owner?.name || "—"}
                        </p>
                        <p className="text-muted-foreground">
                          {payment.owner?.email || "—"}
                        </p>
                        <p className="text-muted-foreground">
                          {payment.owner?.phone || "—"}
                        </p>
                      </div>

                      <div>
                        <p className="text-xs text-muted-foreground">
                          Plan
                        </p>
                        <p className="font-medium">
                          {payment.plan_name ||
                            payment.plan_code ||
                            "—"}
                        </p>
                        <p className="text-muted-foreground">
                          {payment.billing_interval || "—"}
                        </p>
                      </div>

                      <div>
                        <p className="text-xs text-muted-foreground">
                          Amount
                        </p>
                        <p className="font-semibold">
                          {payment.currency || "IDR"}{" "}
                          {Number(payment.amount || 0).toLocaleString(
                            "id-ID"
                          )}
                        </p>
                        <p className="text-muted-foreground">
                          {payment.payment_method || "—"}
                        </p>
                      </div>

                      <div>
                        <p className="text-xs text-muted-foreground">
                          Submitted
                        </p>
                        <p>
                          {payment.payment_submitted_at
                            ? new Date(
                                payment.payment_submitted_at
                              ).toLocaleString("id-ID")
                            : payment.created_at
                            ? new Date(
                                payment.created_at
                              ).toLocaleString("id-ID")
                            : "—"}
                        </p>
                      </div>
                    </div>

                    {status === "REJECTED" &&
                      payment.rejection_reason && (
                        <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-3">
                          <p className="text-xs font-medium text-red-400">
                            Rejection reason
                          </p>
                          <p className="mt-1 text-sm text-red-200">
                            {payment.rejection_reason}
                          </p>
                        </div>
                      )}

                    <div className="grid gap-3 text-xs text-muted-foreground sm:grid-cols-2">
                      <div>
                        <span className="font-medium text-foreground">
                          Invoice:
                        </span>{" "}
                        <span className="break-all font-mono">
                          {payment.id || "—"}
                        </span>
                      </div>

                      <div>
                        <span className="font-medium text-foreground">
                          Merchant Ref:
                        </span>{" "}
                        <span className="break-all font-mono">
                          {payment.merchant_ref || "—"}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
